/**
 * Read a PDF file and return a base64 data URL.
 * Validates MIME and max size.
 */
export async function readPdfAsDataUrl(file, maxSizeMB = 7) {
  if (!file) return null;
  const isPdf =
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf");
  if (!isPdf) {
    throw new Error("El archivo debe ser un PDF.");
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(`El PDF es muy grande (máx ${maxSizeMB} MB).`);
  }
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a base64 data URL to a Blob (works for any size).
 */
function dataUrlToBlob(dataUrl) {
  const match = /^data:([^;]+);base64,(.*)$/.exec(dataUrl || "");
  if (!match) return null;
  const mime = match[1];
  const b64 = match[2];
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Trigger a browser download of a base64 data URL with a given filename.
 * Uses Blob URL for reliability across browsers and large files.
 */
export function downloadDataUrl(dataUrl, filename = "ficha-tecnica.pdf") {
  if (!dataUrl) return false;
  try {
    const blob = dataUrlToBlob(dataUrl);
    if (!blob) {
      // Fallback to direct data URL
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke the URL after a delay to ensure download starts
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    return true;
  } catch (err) {
    console.error("Download failed", err);
    return false;
  }
}

/**
 * Open a base64 PDF in a new tab (alternative to download).
 */
export function openDataUrlInNewTab(dataUrl) {
  if (!dataUrl) return false;
  try {
    const blob = dataUrlToBlob(dataUrl);
    if (!blob) return false;
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return !!win;
  } catch (err) {
    console.error("Open failed", err);
    return false;
  }
}
